import { GoogleMap, InfoWindow, Marker, Polyline, Circle } from "@react-google-maps/api";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";



import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";
import JobMarkerInfoWindow from "../JobMarkerInfoWindow/JobMarkerInfoWindow";


const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 8px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
`;

const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "8px",
};

const defaultCenter = {
  lat: 40.7128,
  lng: -74.006,
};

const options = {
  mapId: process.env.REACT_APP_JOB_SITES_MAP_ID,
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: true,
  fullscreenControl: true,
};

// Create custom marker SVG with red color and first assignee initials
const createCustomMarker = (assignee) => {
  // Get first two letters from assignee
  let initials = "MD";
  if (assignee && assignee.first_name && assignee.last_name) {
    initials = `${assignee.first_name.charAt(0)}${assignee.last_name.charAt(0)}`.toUpperCase();
  } else if (assignee && assignee.first_name) {
    initials = assignee.first_name.substring(0, 2).toUpperCase();
  }

  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 32 44">
      <!-- Marker shape -->
      <path fill="#E74335" d="M16 0C7.16 0 0 7.16 0 16c0 11.2 16 28 16 28s16-16.8 16-28C32 7.16 24.84 0 16 0z"/>
      <!-- Initials text -->
      <text x="17" y="16" dy="0.1em" font-family="'Mona Sans', sans-serif" font-size="13" font-weight="700" fill="#FFFFFF" text-anchor="middle" dominant-baseline="middle" letter-spacing="2">
        ${initials}
      </text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

// Spiderfy markers with same location by offsetting them in a circular pattern
const spiderfyMarkers = (markers) => {
  if (!markers || markers.length === 0) return markers;
  
  // Group markers by location (rounded to 6 decimal places to handle floating point)
  const locationGroups = {};
  
  markers.forEach(marker => {
    const key = `${marker.location.lat.toFixed(6)},${marker.location.lng.toFixed(6)}`;
    if (!locationGroups[key]) {
      locationGroups[key] = [];
    }
    locationGroups[key].push(marker);
  });
  
  // Process each group
  const spiderfiedMarkers = [];
  
  Object.values(locationGroups).forEach(group => {
    if (group.length === 1) {
      // No overlap, keep original position
      spiderfiedMarkers.push(group[0]);
    } else {
      // Multiple markers at same location - spread them in a circle
      const radius = 0.0002; // Approximately 22 meters
      const angleStep = (2 * Math.PI) / group.length;
      
      group.forEach((marker, index) => {
        const angle = angleStep * index;
        const offsetLat = radius * Math.cos(angle);
        const offsetLng = radius * Math.sin(angle);
        
        spiderfiedMarkers.push({
          ...marker,
          location: {
            lat: marker.location.lat + offsetLat,
            lng: marker.location.lng + offsetLng,
          },
          originalLocation: {
            lat: marker.location.lat,
            lng: marker.location.lng,
          },
          isSpiderfied: true, // Mark as spiderfied
        });
      });
    }
  });
  
  return spiderfiedMarkers;
};

// Get unique center locations for spiderfied markers
const getSpiderfiedCenters = (markers) => {
  const centers = {};
  
  markers.forEach(marker => {
    if (marker.originalLocation) {
      const key = `${marker.originalLocation.lat.toFixed(6)},${marker.originalLocation.lng.toFixed(6)}`;
      if (!centers[key]) {
        centers[key] = marker.originalLocation;
      }
    }
  });
  
  return Object.values(centers);
};

const JobsInGoogleMap = ({ map, setMap, jobMarkers = [], isLoadingJobs = false, orgTimezone, selectedJobs = [] }) => {
  const boundsRef = useRef(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [spiderfiedMarkers, setSpiderfiedMarkers] = useState([]);
  const [spiderfiedCenters, setSpiderfiedCenters] = useState([]);

  // Apply spiderfy effect when markers change
  useEffect(() => {
    const spiderfied = spiderfyMarkers(jobMarkers);
    setSpiderfiedMarkers(spiderfied);
    
    // Get centers for spiderfied markers
    const centers = getSpiderfiedCenters(spiderfied);
    setSpiderfiedCenters(centers);
  }, [jobMarkers]);

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const onLoad = mapInstance => {
    // Attach marker click handler to map instance for external access
    mapInstance.markerClickHandler = handleMarkerClick;
    setMap(mapInstance);
  };

  const onUnmount = () => {
    setMap(null);
  };

  // Auto-fit map bounds when markers change
  useEffect(() => {
    if (map && spiderfiedMarkers.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      
      spiderfiedMarkers.forEach(marker => {
        if (marker.location && marker.location.lat && marker.location.lng) {
          bounds.extend(marker.location);
        }
      });

      map.fitBounds(bounds);

      // Add some padding
      const padding = { top: 50, right: 50, bottom: 50, left: 50 };
      map.fitBounds(bounds, padding);

      // Prevent zooming in too much for single markers
      const listener = window.google.maps.event.addListener(map, "idle", () => {
        if (map.getZoom() > 15) {
          map.setZoom(15);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [map, spiderfiedMarkers]);

  // Auto-open InfoWindow when a job is selected from the list
  useEffect(() => {
    if (selectedJobs && selectedJobs.length > 0 && spiderfiedMarkers.length > 0) {
      // Get the selected job (single selection)
      const selectedJobId = selectedJobs[0];
      const markerToOpen = spiderfiedMarkers.find(marker => marker.id === selectedJobId);
      
      if (markerToOpen) {
        handleMarkerClick(markerToOpen);
      }
    } else {
      // Clear selection when no jobs selected
      setSelectedMarker(null);
    }
  }, [selectedJobs, spiderfiedMarkers]);

  // Small vertical pixel offset to render InfoWindow slightly above the marker
  const infoWindowPixelOffset =
    typeof window !== "undefined" && window.google
      ? new window.google.maps.Size(0, -40)
      : null;

  return (
    <MapContainer>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={options}
      >
        {/* Draw lines connecting spiderfied markers to their center */}
        {spiderfiedMarkers && spiderfiedMarkers.length > 0 && (
          spiderfiedMarkers
            .filter(marker => marker.originalLocation)
            .map((marker) => (
              <Polyline
                key={`line-${marker.id}`}
                path={[
                  marker.originalLocation,
                  marker.location,
                ]}
                options={{
                  strokeColor: "#2266EF",
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                  geodesic: false,
                }}
              />
            ))
        )}

        {/* Draw circles at center of spiderfied groups */}
        {spiderfiedCenters && spiderfiedCenters.length > 0 && (
          spiderfiedCenters.map((center, index) => (
            <Circle
              key={`center-${index}`}
              center={center}
              radius={8} // 8 meters radius
              options={{
                fillColor: "#2266EF",
                fillOpacity: 0.4,
                strokeColor: "#2266EF",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: false,
              }}
            />
          ))
        )}

        {/* Render markers */}
        {spiderfiedMarkers && spiderfiedMarkers.length > 0 && (
          spiderfiedMarkers.map((marker) => {
            // Get first assignee from job
            const firstAssignee = marker.job && marker.job.assignees && marker.job.assignees.length > 0 
              ? marker.job.assignees[0] 
              : null;
            
            return (
              <Marker
                key={marker.id}
                position={marker.location}
                title={marker.title || "Job"}
                icon={createCustomMarker(firstAssignee)}
                onClick={() => handleMarkerClick(marker)}
              />
            );
          })
        )}

        {selectedMarker && (
          <InfoWindow
            position={selectedMarker.originalLocation || selectedMarker.location}
            options={infoWindowPixelOffset ? { pixelOffset: infoWindowPixelOffset } : undefined}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <JobMarkerInfoWindow job={selectedMarker.job} orgTimezone={orgTimezone} />
          </InfoWindow>
        )}
      </GoogleMap>
      {isLoadingJobs && (
        <LoadingOverlay>
          <ComponentCircleLoader />
        </LoadingOverlay>
      )}
    </MapContainer>
  );
};

export default JobsInGoogleMap;